import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users } from 'lucide-react';
import { useBannerClick, useBannerImpression } from './adPreview';
import type { PromoControls } from './adBanners';

/**
 * „In eigener Sache"-Banner im Landing-Feed: Vereine und Anbieter sollen ihre Veranstalterseite
 * übernehmen. Belegt den Slot unter „n EVENTS", den zuvor der Tour-de-France- und danach der
 * Social-Banner hatten (`docs/werbung-spezifikation.md`, Abschnitt 2).
 *
 * Bewusst eine gebaute Karte und kein Bild: das 2060×190-Motiv würde bei 1024 px Viewport auf
 * 614 px skaliert und die Headline auf 12 px schrumpfen. Als Karte bleibt der Text echt und
 * skaliert mit der Spalte.
 *
 * Ziel ist `/fuer-veranstalter?via=banner`; dort feuert `offer_viewed` mit dem Eingang `banner`,
 * die Geschäftszahlen zeigen so, wie viele Besucher der Angebotsseite vom Banner kommen. Zusätzlich Einblendung und Klick als
 * `AD_BANNER`-Slot `organizer-promo`, damit die Klickrate unter /admin/tracking steht.
 *
 * Der Text spricht den Verein im Plural an („eure Events"): gemeint ist der Verein, nicht die
 * Person, die gerade liest. Und er nennt keine Funktionen, weil ein Veranstalter, der die Seite
 * nicht kennt, mit „Logo, Bild, Flyer" nichts anfangen kann.
 */
const SLOT = 'organizer-promo';

const eyebrow: React.CSSProperties = { fontFamily: "'Space Mono',monospace", letterSpacing: '0.14em', color: '#D08BA3' };
const headline: React.CSSProperties = { fontFamily: "'Archivo',sans-serif", fontWeight: 800, color: '#fff', lineHeight: 1.1 };
const subline: React.CSSProperties = { fontFamily: "'Hanken Grotesk',sans-serif", color: '#D8CDD3' };

function Cta({ placement, style }: { placement: string; style?: React.CSSProperties }) {
  const trackClick = useBannerClick(SLOT);
  return (
    <Link
      to="/fuer-veranstalter?via=banner"
      onClick={() => trackClick(placement)}
      className="organizer-promo-btn"
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#fff', color: '#922C57', fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, fontSize: 14, padding: '12px 20px', borderRadius: 999, whiteSpace: 'nowrap', textDecoration: 'none', ...style }}
    >
      Ich bin Veranstalter <ArrowRight size={16} aria-hidden />
    </Link>
  );
}

/**
 * `controls` zeichnet der Slot: Pfeile am linken und rechten Rand, die Punkte mittig unten. Auf
 * dem Handy stehen beide als eigene Zeile unter dem Knopf. Steht die Anzeige allein (Design-Lab),
 * bleibt der Platz leer und die Karte sieht aus wie eine gebuchte Platzierung.
 */
export default function OrganizerPromoBanner({ controls }: { controls?: PromoControls }) {
  useBannerImpression(SLOT);

  // Ohne Bedienelemente bleibt die Karte, wie sie in der Spezifikation steht; mit ihnen braucht
  // sie links und rechts Platz für die Pfeile und unten für die Punkte.
  const desktopPadding = controls ? '14px 46px 24px 46px' : '16px 22px 16px 20px';

  // Beide Anzeigen sind auf dem Handy gleich hoch, sonst rückt die Eventliste bei jedem Wechsel
  // ein Stück nach oben (Gegenstück in `DeltaBikePromoBanner.css`). Kompakt, weil die Anzeige auf
  // dem Handy zwischen den Event-Cards steht. Die Punkte liegen im unteren Rand, daher dort mehr Abstand.
  const mobileCard: React.CSSProperties = controls ? { minHeight: 172, paddingBottom: 24 } : {};

  return (
    <>
      {/* Desktop-Leaderboard */}
      <div
        className="hide-mobile"
        style={{ position: 'relative', overflow: 'hidden', borderRadius: 12, background: 'linear-gradient(100deg,#2C2C2C 0%,#6E2247 62%,#922C57 100%)', padding: desktopPadding, display: 'flex', alignItems: 'center', gap: 18 }}
      >
        <div style={{ position: 'absolute', right: -30, top: -30, width: 130, height: 130, background: '#C25078', opacity: 0.14, transform: 'rotate(45deg)', borderRadius: 26, pointerEvents: 'none' }} />
        <span className="promo-banner-icon" style={{ flexShrink: 0, width: 52, height: 52, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 14, background: 'rgba(255,255,255,0.12)' }}>
          <Users size={26} strokeWidth={1.8} color="#fff" aria-hidden />
        </span>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <div style={{ ...eyebrow, fontSize: 10 }}>FÜR VERANSTALTER</div>
          <div style={{ ...headline, fontSize: 21, marginTop: 5 }}>Mehr Leute am Start.</div>
          <div style={{ ...subline, fontSize: 13, marginTop: 3 }}>Holt das meiste aus euren Events raus und pflegt sie künftig selbst.</div>
        </div>
        <Cta placement="landing-desktop" style={{ position: 'relative', flexShrink: 0 }} />
        {controls?.('desktop')}
      </div>

      {/* Handy-Karte */}
      <div
        className="hide-desktop promo-card-mobile"
        style={{ position: 'relative', overflow: 'hidden', borderRadius: 14, background: 'linear-gradient(150deg,#2C2C2C 0%,#6E2247 70%,#922C57 120%)', padding: '14px 16px', ...mobileCard }}
      >
        <div style={{ position: 'absolute', right: -34, top: -34, width: 130, height: 130, background: '#C25078', opacity: 0.16, transform: 'rotate(45deg)', borderRadius: 26, pointerEvents: 'none' }} />
        <div style={{ ...eyebrow, fontSize: 10, letterSpacing: '0.12em', position: 'relative' }}>FÜR VERANSTALTER</div>
        <div style={{ ...headline, fontSize: 18, margin: '6px 0 3px', position: 'relative' }}>Mehr Leute am Start.</div>
        <div className="promo-card-subline" style={{ ...subline, fontSize: 13, lineHeight: 1.35, position: 'relative' }}>Holt das meiste aus euren Events raus und pflegt sie künftig selbst.</div>
        <div className="promo-card-action" style={{ position: 'relative', marginTop: 10 }}>
          <Cta placement="landing-mobile" style={{ width: '100%', padding: '9px 20px' }} />
        </div>
        {controls?.('mobile')}
      </div>
    </>
  );
}
